import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Paper, Button, 
  Accordion, AccordionSummary, AccordionDetails, IconButton, 
  Chip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, 
  DialogActions, DialogContentText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  TextFields as TextIcon,
  VideoLibrary as VideoIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Quiz as QuizIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import api from '../utils/api';
import SectionForm from '../components/SectionForm';
import ContentBlockForm from '../components/ContentBlockForm';
import TrainingForm from '../components/TrainingForm';
import RevisionConfirmDialog from '../components/RevisionConfirmDialog';

interface ContentBlock {
  id: number;
  block_type: string;
  title: string;
  content: any;
  order: number;
  is_required: boolean;
  section: number; // Add the section property
  questions?: any[]; // Quiz questions for quiz content blocks
}

interface TrainingSection {
  id: number;
  title: string;
  description: string;
  order: number;
  is_required: boolean;
  content_blocks: ContentBlock[];
}

interface Training {
  id: number;
  title: string;
  description: string;
  training_type: string;
  sections: TrainingSection[];
  total_sections: number;
  total_content_blocks: number;
}

const TrainingContent: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null);
  
  // Drag and drop states
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Content block drag and drop states
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(null);
  const [dragOverBlockIndex, setDragOverBlockIndex] = useState<number | null>(null);
  const [draggedBlockSectionId, setDraggedBlockSectionId] = useState<number | null>(null);
  
  // Form states
  const [sectionFormOpen, setSectionFormOpen] = useState(false);
  const [contentBlockFormOpen, setContentBlockFormOpen] = useState(false);
  const [editSection, setEditSection] = useState<TrainingSection | null>(null);
  const [editContentBlock, setEditContentBlock] = useState<ContentBlock | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [nextOrder, setNextOrder] = useState<number | null>(null);
  
  // Training details edit state
  const [trainingFormOpen, setTrainingFormOpen] = useState(false);
  
  // Confirmation modal states
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    itemType: '' as 'section' | 'contentBlock'
  });

  // Revision dialog states
  const [revisionDialog, setRevisionDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    loading: false
  });
  
  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Handle navigation state from Trainings page
  useEffect(() => {
    if (location.state?.selectedTrainingId) {
      fetchTraining(location.state.selectedTrainingId);
    }
  }, [location.state]);



  const fetchTraining = async (trainingId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/trainings/${trainingId}/`);
      const training = response.data;
      
      // Debug: Check quiz questions in the response
      console.log('DEBUG: Training fetch response:', training);
      const quizBlocks = training.sections?.flatMap((section: any) => 
        section.content_blocks?.filter((block: any) => block.block_type === 'quiz' || block.block_type === 'test') || []
      ) || [];
      console.log('DEBUG: Quiz blocks found:', quizBlocks);
      quizBlocks.forEach((block: any, index: number) => {
        console.log(`DEBUG: Quiz block ${index + 1} questions:`, block.questions || []);
      });
      
      setSelectedTraining(training);
    } catch (error) {
      console.error('Failed to fetch training:', error);
      showSnackbar('Failed to fetch training', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getApiErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data.error === 'string') return data.error;
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data === 'object') {
      const parts = Object.entries(data).map(([key, value]) => {
        if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
        if (typeof value === 'string') return value;
        return `${key}: ${JSON.stringify(value)}`;
      });
      if (parts.length) return parts.join('; ');
    }
    return fallback;
  };

  const mapSectionIdToRevision = (
    oldTraining: Training,
    newTraining: Training,
    oldSectionId: number
  ): number | null => {
    const oldSection = oldTraining.sections.find((s) => s.id === oldSectionId);
    if (!oldSection) {
      return newTraining.sections[0]?.id ?? null;
    }
    const newSection = newTraining.sections.find(
      (s) => s.order === oldSection.order && s.title === oldSection.title
    );
    return newSection?.id ?? null;
  };

  const createRevisionAndContinue = async (
    oldTraining: Training,
    afterRevision: (newTraining: Training) => Promise<void>
  ) => {
    const resp = await api.post(`/trainings/${oldTraining.id}/create_revision/`);
    const newTraining = resp.data?.new_training as Training | undefined;
    if (!newTraining?.id) {
      throw new Error('Revision was created but the new training version could not be loaded.');
    }
    await afterRevision(newTraining);
    setSelectedTraining(newTraining);
    await fetchTraining(newTraining.id);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showRevisionDialog = (title: string, message: string, onConfirm: () => void) => {
    setRevisionDialog({
      open: true,
      title,
      message,
      onConfirm,
      loading: false
    });
  };

  const handleRevisionConfirm = async () => {
    setRevisionDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await revisionDialog.onConfirm();
      setRevisionDialog(prev => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      setRevisionDialog(prev => ({ ...prev, loading: false }));
      showSnackbar('Failed to create training revision', 'error');
    }
  };

  const handleRevisionCancel = () => {
    setRevisionDialog(prev => ({ ...prev, open: false, loading: false }));
  };

  // Section handlers
  const handleAddSection = () => {
    // Calculate next order for sections
    const nextOrder = selectedTraining ? Math.max(...selectedTraining.sections.map(s => s.order), 0) + 1 : 1;
    
    setEditSection(null);
    setSectionFormOpen(true);
    setNextOrder(nextOrder);
  };

  const handleEditTrainingDetails = () => {
    setTrainingFormOpen(true);
  };

  const handleTrainingFormClose = () => {
    setTrainingFormOpen(false);
  };

  const handleTrainingFormSubmit = async (data: any) => {
    try {
      await api.put(`/trainings/${selectedTraining!.id}/`, data);
      await fetchTraining(selectedTraining!.id);
      showSnackbar('Training details updated successfully');
      setTrainingFormOpen(false);
    } catch (error: any) {
      console.error('Failed to update training:', error);
      
      // Check if this is a revision requirement error
      if (error.response?.data?.requires_revision) {
        showRevisionDialog(
          'Training Revision Required',
          'This training has been started by users. Creating a revision will preserve the original version while creating a new editable version.',
          async () => {
            await createRevisionAndContinue(selectedTraining!, async () => {
              showSnackbar('Training revision created successfully');
            });
            setTrainingFormOpen(false);
          }
        );
      } else {
        showSnackbar('Failed to update training details', 'error');
      }
    }
  };

  const handleEditSection = (section: TrainingSection) => {
    setEditSection(section);
    setSectionFormOpen(true);
  };

  const handleSectionSubmit = async (data: any) => {
    try {
      const sectionData = {
        ...data,
        training: selectedTraining?.id,
        order: editSection ? editSection.order : nextOrder
      };
      
      if (editSection) {
        // Update existing section
        await api.put(`/training-sections/${editSection.id}/`, sectionData);
        showSnackbar('Section updated successfully');
      } else {
        // Create new section
        await api.post('/training-sections/', sectionData);
        showSnackbar('Section created successfully');
      }
      
      // Refresh the training data
      if (selectedTraining) {
        await fetchTraining(selectedTraining.id);
      }
      
      setSectionFormOpen(false);
      setEditSection(null);
    } catch (error: any) {
      console.error('Error saving section:', error);
      
      // Check if this is a revision requirement error
      if (error.response?.data?.requires_revision) {
        showRevisionDialog(
          'Training Revision Required',
          'This training has been started by users. Creating a revision will preserve the original version while creating a new editable version.',
          async () => {
            await createRevisionAndContinue(selectedTraining!, async () => {
              showSnackbar('Training revision created successfully');
            });
            setSectionFormOpen(false);
            setEditSection(null);
          }
        );
      } else {
        showSnackbar('Error saving section', 'error');
      }
    }
  };

  // Content block handlers
  const handleAddContentBlock = (sectionId: number) => {
    // Find the section and calculate next order
    const section = selectedTraining?.sections.find(s => s.id === sectionId);
    const orders = section?.content_blocks?.map((b) => b.order) ?? [];
    const nextOrder = section ? Math.max(...orders, 0) + 1 : 1;
    
    setSelectedSectionId(sectionId);
    setEditContentBlock(null);
    setContentBlockFormOpen(true);
    setNextOrder(nextOrder);
  };

  const handleEditContentBlock = (block: ContentBlock) => {
    setEditContentBlock(block);
    setSelectedSectionId(block.section);
    setContentBlockFormOpen(true);
  };

  const saveContentBlock = async (training: Training, blockData: any) => {
    if (editContentBlock) {
      await api.put(`/content-blocks/${editContentBlock.id}/`, blockData);
      showSnackbar('Content block updated successfully');
    } else {
      await api.post('/content-blocks/', blockData);
      showSnackbar('Content block created successfully');
    }
    await fetchTraining(training.id);
    setContentBlockFormOpen(false);
    setEditContentBlock(null);
    setSelectedSectionId(null);
    setNextOrder(null);
  };

  const handleContentBlockSubmit = async (data: any) => {
    if (!selectedTraining || !selectedSectionId) {
      showSnackbar('Select a section before adding a content block', 'error');
      throw new Error('No section selected');
    }

    const blockData = {
      ...data,
      section: selectedSectionId,
      order: editContentBlock ? editContentBlock.order : nextOrder,
    };

    try {
      await saveContentBlock(selectedTraining, blockData);
    } catch (error: any) {
      console.error('Error saving content block:', error);

      if (error.response?.data?.requires_revision) {
        const oldTraining = selectedTraining;
        const pendingBlockData = { ...blockData };

        showRevisionDialog(
          'Training Revision Required',
          'This training has been started by users. We will create a new revision and keep the content block edit from this dialog.',
          async () => {
            await createRevisionAndContinue(oldTraining, async (newTraining) => {
              let revision = newTraining;
              if (!revision.sections?.length) {
                const fetched = await api.get(`/trainings/${revision.id}/`);
                revision = fetched.data;
              }
              const newSectionId = mapSectionIdToRevision(
                oldTraining,
                revision,
                pendingBlockData.section
              );
              if (!newSectionId) {
                throw new Error('Could not find the matching section on the new training revision.');
              }
              const newSection = revision.sections.find((section) => section.id === newSectionId);
              const originalOrder = editContentBlock?.order ?? pendingBlockData.order;
              const copiedBlock = editContentBlock
                ? newSection?.content_blocks?.find((block) => block.order === originalOrder)
                : undefined;

              const blockPayload = {
                section: newSectionId,
                block_type: pendingBlockData.block_type,
                title: pendingBlockData.title,
                content: pendingBlockData.content,
                order: copiedBlock?.order ?? pendingBlockData.order,
                is_required: pendingBlockData.is_required,
                passing_score: pendingBlockData.passing_score ?? null,
              };

              if (copiedBlock) {
                await api.patch(`/content-blocks/${copiedBlock.id}/`, blockPayload);
              } else {
                await api.post('/content-blocks/', blockPayload);
              }
              showSnackbar('Training revision created successfully');
              setContentBlockFormOpen(false);
              setEditContentBlock(null);
              setSelectedSectionId(null);
              setNextOrder(null);
            });
          }
        );
        return;
      }

      const message = getApiErrorMessage(error, 'Error saving content block');
      showSnackbar(message, 'error');
      throw error;
    }
  };

  const handleDeleteSection = async (section: TrainingSection) => {
    setConfirmModal({
      open: true,
      title: 'Delete Section',
      message: `Are you sure you want to delete the section "${section.title}"? This will also delete all content blocks within this section.`,
      onConfirm: async () => {
        try {
          await api.delete(`/training-sections/${section.id}/`);
          showSnackbar('Section deleted successfully');
          
          // Optimistically remove the section from the UI
          if (selectedTraining) {
            const updatedTraining = {
              ...selectedTraining,
              sections: selectedTraining.sections.filter(s => s.id !== section.id)
            };
            
            setSelectedTraining(updatedTraining);
          }
          
          // Fetch the updated training to ensure we have the latest data
          if (selectedTraining) {
            await fetchTraining(selectedTraining.id);
          }
        } catch (error: any) {
          console.error('Failed to delete section:', error);
          showSnackbar('Failed to delete section', 'error');
        }
        setConfirmModal(prev => ({ ...prev, open: false }));
      },
      itemType: 'section'
    });
  };

  const handleDeleteContentBlock = async (block: ContentBlock) => {
    setConfirmModal({
      open: true,
      title: 'Delete Content Block',
      message: `Are you sure you want to delete "${block.title}"?`,
      onConfirm: async () => {
        try {
          await api.delete(`/content-blocks/${block.id}/`);
          
          // Optimistically update UI
          if (selectedTraining) {
            const updatedTraining = {
              ...selectedTraining,
              sections: selectedTraining.sections.map(section => ({
                ...section,
                content_blocks: section.content_blocks.filter(b => b.id !== block.id)
              }))
            };
            setSelectedTraining(updatedTraining);
          }
          
          showSnackbar('Content block deleted successfully');
          setConfirmModal(prev => ({ ...prev, open: false }));
        } catch (error: any) {
          console.error('Failed to delete content block:', error);
          if (error.response) {
            console.error('Response data:', error.response.data);
            console.error('Response status:', error.response.status);
          }
          showSnackbar('Failed to delete content block', 'error');
          setConfirmModal(prev => ({ ...prev, open: false }));
        }
      },
      itemType: 'contentBlock'
    });
  };

  // Custom drag handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = async (e: React.DragEvent) => {
    e.preventDefault();
    
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex && selectedTraining) {
      
      // Reorder sections
      const reorderedSections = Array.from(selectedTraining.sections);
      const [removed] = reorderedSections.splice(draggedIndex, 1);
      reorderedSections.splice(dragOverIndex, 0, removed);

      // Update order numbers
      const updatedSections = reorderedSections.map((section, index) => ({
        ...section,
        order: index + 1
      }));

      const updatedTraining = {
        ...selectedTraining,
        sections: updatedSections
      };

      // Optimistically update UI
      setSelectedTraining(updatedTraining);

      // Update backend - update orders sequentially to avoid unique constraint violations
      try {
        // First, set all orders to temporary high values to avoid conflicts
        const tempOrders = updatedSections.map((section, index) => ({
          id: section.id,
          order: 1000 + index // Use high temporary values
        }));
        
        await Promise.all(
          tempOrders.map(({ id, order }) => 
            api.patch(`/training-sections/${id}/`, { order })
          )
        );
        
        // Then set the final orders
        await Promise.all(
          updatedSections.map(section => 
            api.patch(`/training-sections/${section.id}/`, { order: section.order })
          )
        );
        
        showSnackbar('Sections reordered successfully');
      } catch (error) {
        console.error('Failed to reorder sections:', error);
        showSnackbar('Failed to reorder sections', 'error');
        // Revert on error
        await fetchTraining(selectedTraining.id);
      }
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  // Content block drag handlers
  const handleBlockDragStart = (blockIndex: number, sectionId: number) => {
    setDraggedBlockIndex(blockIndex);
    setDraggedBlockSectionId(sectionId);
  };

  const handleBlockDragOver = (e: React.DragEvent, blockIndex: number) => {
    e.preventDefault();
    setDragOverBlockIndex(blockIndex);
  };

  const handleBlockDragEnd = async (e: React.DragEvent) => {
    e.preventDefault();
    
    if (draggedBlockIndex !== null && dragOverBlockIndex !== null && 
        draggedBlockIndex !== dragOverBlockIndex && draggedBlockSectionId !== null && selectedTraining) {
      
      // Find the section
      const sectionIndex = selectedTraining.sections.findIndex(s => s.id === draggedBlockSectionId);
      if (sectionIndex === -1) {
        console.error('Section not found');
        return;
      }
      
      const section = selectedTraining.sections[sectionIndex];
      
      // Reorder content blocks
      const reorderedBlocks = Array.from(section.content_blocks);
      const [removed] = reorderedBlocks.splice(draggedBlockIndex, 1);
      reorderedBlocks.splice(dragOverBlockIndex, 0, removed);

      // Update order numbers
      const updatedBlocks = reorderedBlocks.map((block, index) => ({
        ...block,
        order: index + 1
      }));

      const updatedSection = {
        ...section,
        content_blocks: updatedBlocks
      };

      const updatedSections = [...selectedTraining.sections];
      updatedSections[sectionIndex] = updatedSection;

      const updatedTraining = {
        ...selectedTraining,
        sections: updatedSections
      };

      // Optimistically update UI
      setSelectedTraining(updatedTraining);

      // Update backend - update orders sequentially to avoid unique constraint violations
      try {
        // First, set all orders to temporary high values to avoid conflicts
        const tempOrders = updatedBlocks.map((block, index) => ({
          id: block.id,
          order: 1000 + index // Use high temporary values
        }));
        
        await Promise.all(
          tempOrders.map(({ id, order }) => 
            api.patch(`/content-blocks/${id}/`, { order })
          )
        );
        
        // Then set the final orders
        await Promise.all(
          updatedBlocks.map(block => 
            api.patch(`/content-blocks/${block.id}/`, { order: block.order })
          )
        );
        
        showSnackbar('Content blocks reordered successfully');
      } catch (error) {
        console.error('Failed to reorder content blocks:', error);
        showSnackbar('Failed to reorder content blocks', 'error');
        // Revert on error
        await fetchTraining(selectedTraining.id);
      }
    }
    
    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
    setDraggedBlockSectionId(null);
  };

  const handleBlockDrop = (e: React.DragEvent, blockIndex: number) => {
    e.preventDefault();
    setDragOverBlockIndex(blockIndex);
  };

  const getBlockTypeIcon = (blockType: string) => {
    switch (blockType) {
      case 'text': return <TextIcon />;
      case 'video': return <VideoIcon />;
      case 'link': return <LinkIcon />;
      case 'image': return <ImageIcon />;
      case 'quiz': return <QuizIcon />;
      default: return <TextIcon />;
    }
  };

  const getBlockTypeColor = (blockType: string) => {
    switch (blockType) {
      case 'text': return 'primary';
      case 'video': return 'secondary';
      case 'link': return 'info';
      case 'image': return 'success';
      case 'quiz': return 'error';
      default: return 'default';
    }
  };

  const formatContentPreview = (block: ContentBlock) => {
    switch (block.block_type) {
      case 'text':
        return block.content.text?.substring(0, 100) + '...' || 'No text content';
      case 'video':
        return block.content.video_title || 'Video content';
      case 'link':
        return block.content.link_title || 'External link';
      case 'image':
        return block.content?.caption || block.content?.image_url || 'Image content';
      case 'quiz':
        const questionCount = block.questions?.length || 0;
        const passingScore = block.content?.passing_score;
        let preview = `${questionCount} question${questionCount !== 1 ? 's' : ''}`;
        if (passingScore) preview += `, ${passingScore}% to pass`;
        return preview;
      case 'test':
        const testQuestionCount = block.questions?.length || 0;
        const testPassingScore = block.content?.passing_score;
        let testPreview = `${testQuestionCount} question${testQuestionCount !== 1 ? 's' : ''}`;
        if (testPassingScore) testPreview += `, ${testPassingScore}% to pass`;
        return testPreview;
      default:
        return 'Content';
    }
  };

  if (loading) {
    return <Typography>Loading training...</Typography>;
  }

  if (!selectedTraining) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Training Selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please select a training from the Trainings page to manage its content
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h4" gutterBottom>Training Content Management</Typography>
      
      <Grid container spacing={3} sx={{ width: '100%', maxWidth: '100%' }}>
        {/* Training Content */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">{selectedTraining.title}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditTrainingDetails}>
                    Edit Details
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddSection}>
                    Add Section
                  </Button>
                </Box>
              </Box>
              
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {selectedTraining.description}
              </Typography>

              {selectedTraining.sections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No sections yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create your first section to start building training content
                  </Typography>
                </Box>
              ) : (
                <div>
                  {selectedTraining.sections.filter(section => section.id).map((section, index) => (
                    <Accordion key={section.id} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                              cursor: 'grab',
                              marginRight: '8px',
                              opacity: draggedIndex === index ? 0.5 : 1,
                            }}
                          >
                            <DragIcon />
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6">
                              {section.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {section.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Chip 
                                label={`${section.content_blocks.length} blocks`} 
                                size="small" 
                                variant="outlined" 
                              />
                              {section.is_required && (
                                <Chip label="Required" size="small" color="error" />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddContentBlock(section.id);
                              }}
                              title="Add Content Block"
                            >
                              <AddIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSection(section);
                              }}
                              title="Edit Section"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSection(section);
                              }}
                              title="Delete Section"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {section.content_blocks.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              No content blocks yet
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddContentBlock(section.id)}
                            >
                              Add Content Block
                            </Button>
                          </Box>
                        ) : (
                          <Grid container spacing={2}>
                            {section.content_blocks.map((block, blockIndex) => (
                              <Grid item xs={12} sm={6} md={4} key={block.id}>
                                <Card 
                                  draggable
                                  onDragStart={() => handleBlockDragStart(blockIndex, section.id)}
                                  onDragOver={(e) => handleBlockDragOver(e, blockIndex)}
                                  onDrop={(e) => handleBlockDrop(e, blockIndex)}
                                  onDragEnd={handleBlockDragEnd}
                                  sx={{ 
                                    height: '100%',
                                    border: `2px solid ${getBlockTypeColor(block.block_type)}`,
                                    position: 'relative',
                                    cursor: 'grab',
                                    opacity: draggedBlockIndex === blockIndex && draggedBlockSectionId === section.id ? 0.5 : 1,
                                    transform: draggedBlockIndex === blockIndex && draggedBlockSectionId === section.id ? 'rotate(5deg)' : 'none',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: draggedBlockIndex === blockIndex && draggedBlockSectionId === section.id ? '#e3f2fd' : 
                                                   dragOverBlockIndex === blockIndex && draggedBlockSectionId === section.id ? '#fff3e0' : 'background.paper'
                                  }}
                                >
                                  <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                      <Box
                                        sx={{
                                          cursor: 'grab',
                                          marginRight: '8px',
                                          opacity: 0.6,
                                          '&:hover': { opacity: 1 }
                                        }}
                                      >
                                        <DragIcon fontSize="small" />
                                      </Box>
                                      {getBlockTypeIcon(block.block_type)}
                                      <Typography variant="subtitle2" sx={{ ml: 1, flexGrow: 1 }}>
                                        {block.title}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleEditContentBlock(block)}
                                          title="Edit Content Block"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteContentBlock(block)}
                                          title="Delete Content Block"
                                          color="error"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                    
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      {formatContentPreview(block)}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      <Chip 
                                        label={block.block_type} 
                                        size="small" 
                                        sx={{ 
                                          bgcolor: getBlockTypeColor(block.block_type),
                                          color: 'white'
                                        }}
                                      />
                                      {block.is_required && (
                                        <Chip label="Required" size="small" color="error" />
                                      )}
                                      {(block.block_type === 'quiz' || block.block_type === 'test') && block.content.passing_score && (
                                        <Chip 
                                          label={`${block.content.passing_score}% pass`} 
                                          size="small" 
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </div>
              )}
            </Paper>
        </Grid>
      </Grid>

      {/* Forms */}
      <SectionForm
        open={sectionFormOpen}
        onClose={() => {
          setSectionFormOpen(false);
          setEditSection(null);
          setNextOrder(null);
        }}
        onSubmit={handleSectionSubmit}
        initialData={editSection}
        trainingId={selectedTraining?.id || 0}
        nextOrder={nextOrder || 1}
      />

      <ContentBlockForm
        open={contentBlockFormOpen}
        onClose={() => {
          setContentBlockFormOpen(false);
          setEditContentBlock(null);
          setSelectedSectionId(null);
          setNextOrder(null);
        }}
        onSubmit={handleContentBlockSubmit}
        initialData={editContentBlock}
        sectionId={selectedSectionId || 0}
        nextOrder={nextOrder || 1}
      />

      <TrainingForm
        open={trainingFormOpen}
        onClose={handleTrainingFormClose}
        onSubmit={handleTrainingFormSubmit}
        initialData={selectedTraining}
      />

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmModal.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmModal.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmModal(prev => ({ ...prev, open: false }))}
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmModal.onConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Revision Confirmation Dialog */}
      <RevisionConfirmDialog
        open={revisionDialog.open}
        onClose={handleRevisionCancel}
        onConfirm={handleRevisionConfirm}
        title={revisionDialog.title}
        message={revisionDialog.message}
        loading={revisionDialog.loading}
      />
    </Box>
  );
};

export default TrainingContent; 